import { useId, useState } from 'react';
import { useTokens } from '../theme';
import {
  Stat, StatRow, Note, dollars,
} from '../uiKit';
import {
  fmtHours, fmtSaved, fmtSavedShare, fmtValue, fmtHourly, fmtPerDollar,
} from '../timeSavedFormat.js';

/**
 * Time saved for a school (COF-591, design D10 and "Portal analytics page and
 * console drill-in"), from the envelope's `timeSaved` block. Every figure is
 * the API's: for a school admin it is already bucketed, floored and withheld
 * where small, so a withheld figure reads "Private" and nothing is derived
 * here. Super-admins get exact figures and, with non-program users included,
 * the program split.
 */

const PRIVATE = 'Private';

const rangeLabel = (weeks) => { return weeks === 1 ? 'past week' : `past ${weeks} weeks`; };

const minutesPhrase = (minutes) => { return minutes < 60 ? `${minutes} minutes` : fmtSaved(minutes); };

// A bucketed count the API withheld arrives as null.
const countValue = (value) => { return value == null ? PRIVATE : value.toLocaleString('en-US'); };

const COLUMNS = ['Times', 'Students', 'Hours', 'Minutes each'];

function ActionBreakdown({ rows, minutesEach }) {
  const t = useTokens();
  const cell = { borderBottom: `1px solid ${t.hairline}` };
  const headStyle = { fontFamily: t.fonts.mono, color: t.muted, ...cell };
  const bodyStyle = { fontFamily: t.fonts.mono, color: t.body, ...cell };
  // Each row's values in COLUMNS order. Minutes each are public, from the model.
  const values = (row) => {
    const each = minutesEach[row.key] == null ? 'N/A' : `${minutesEach[row.key]} min`;
    return row.suppressed
      ? [PRIVATE, PRIVATE, PRIVATE, each]
      : [countValue(row.actions), countValue(row.count), fmtHours(row.minutes), each];
  };
  return (
    <div className="mt-4">
      {/* Phones: one card per action (customer tables never scroll sideways). */}
      <ul aria-label="Time saved by action" className="grid gap-2 sm:hidden">
        {rows.map((row) => {
          const rowValues = values(row);
          return (
            <li key={row.key} className="rounded-lg border p-3" style={{ borderColor: t.border, backgroundColor: t.surface }}>
              <div className="text-sm font-semibold" style={{ color: t.heading, fontFamily: t.fonts.body }}>{row.label}</div>
              <dl className="mt-2 grid grid-cols-2 gap-3">
                {COLUMNS.map((term, index) => {
                  return (
                    <div key={term}>
                      <dt className="text-[11px] uppercase tracking-wide" style={{ color: t.muted, fontFamily: t.fonts.mono }}>{term}</dt>
                      <dd className="mt-1 text-sm" style={{ color: t.body, fontFamily: t.fonts.mono }}>{rowValues[index]}</dd>
                    </div>
                  );
                })}
              </dl>
            </li>
          );
        })}
      </ul>
      <table aria-label="Time saved by action" className="hidden w-full border-collapse sm:table">
        <thead>
          <tr>
            <th scope="col" className="py-2 text-left text-xs uppercase tracking-wide font-normal" style={headStyle}>Action</th>
            {COLUMNS.map((label) => {
              return <th key={label} scope="col" className="py-2 pl-3 text-right text-xs uppercase tracking-wide font-normal" style={headStyle}>{label}</th>;
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const rowValues = values(row);
            return (
              <tr key={row.key}>
                <th scope="row" className="py-2 pr-3 text-left text-sm font-normal" style={{ ...bodyStyle, fontFamily: t.fonts.body }}>{row.label}</th>
                {COLUMNS.map((label, index) => {
                  return <td key={label} className="py-2 pl-3 text-right text-sm" style={bodyStyle}>{rowValues[index]}</td>;
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Explainer({ model }) {
  const t = useTokens();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <div className="mt-3">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => { setOpen((value) => { return !value; }); }}
        className={`inline-flex items-center min-h-[44px] text-sm underline underline-offset-4 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
        style={{ color: t.heading, fontFamily: t.fonts.body }}
      >
        How we estimate
      </button>
      {open ? (
        <div id={panelId} className="mt-2 rounded-lg border p-4" style={{ borderColor: t.border, backgroundColor: t.statBg }}>
          <ul className="grid gap-3">
            {model.actions.map((action) => {
              return (
                <li key={action.key}>
                  <div className="text-sm font-semibold" style={{ color: t.heading, fontFamily: t.fonts.body }}>{action.label}</div>
                  <div className="text-sm" style={{ color: t.body, fontFamily: t.fonts.body }}>
                    {`${action.manualMinutes} min by hand, ${action.residualMinutes} min with Coffee Chats, ${action.netMinutes} min saved. Grade ${action.grade}.`}
                  </div>
                </li>
              );
            })}
          </ul>
          <Note className="mt-3">
            Grades: A is government data, B a named survey or study, C a vendor figure, D our assumption. Each figure is the low end of its range.
          </Note>
          <Note className="mt-2">{model.hourlyValueBasis}</Note>
          <Note className="mt-2">
            {`Practice calls, unsent drafts, bounced emails and applications that never went in never count. Counts come from work still in students' accounts, so deleting a contact, a meeting or a job removes its work. Totals cover students currently opted in. Model ${model.version}.`}
          </Note>
        </div>
      ) : null}
    </div>
  );
}

export default function TimeSavedSection({ timeSaved }) {
  const t = useTokens();
  if (!timeSaved || timeSaved.suppressed) { return null; }
  const {
    weeks, allTime, range, roi, model, exact, byPopulation,
  } = timeSaved;
  const everyone = timeSaved.population === 'all';
  const span = rangeLabel(weeks);
  const rangeShown = !range.suppressed;
  const minutesEach = Object.fromEntries(model.actions.map((action) => { return [action.key, action.netMinutes]; }));
  const capacityHours = roi?.capacity ? Math.floor(roi.capacity.minutes / 60) : 0;
  const contract = roi?.contract;
  return (
    <div className="mt-6 border-t pt-5" style={{ borderColor: t.hairline }}>
      <h4 className="text-base font-semibold" style={{ color: t.heading, fontFamily: t.fonts.display }}>Time saved</h4>
      <p className="mt-1 text-sm" style={{ color: t.muted }}>
        {everyone
          ? 'Estimated time Coffee Chats saved everyone with a verified school email on outreach, chat prep and applications, rounded down. Students’ time, not staff time.'
          : 'Estimated time Coffee Chats saved opted-in students on outreach, chat prep and applications, rounded down. Each student counts from when they opted in, and totals cover students currently opted in. Students’ time, not staff time.'}
      </p>
      <div className="mt-4">
        <StatRow>
          <Stat label={`Hours saved, ${span}`} value={rangeShown ? fmtHours(range.totalMinutes) : PRIVATE} />
          <Stat label="Hours saved, all time" value={allTime.suppressed ? PRIVATE : fmtHours(allTime.totalMinutes)} />
          <Stat label={`Per ${everyone ? 'user' : 'student'}, ${span}`} value={rangeShown ? fmtSavedShare(range.perStudentMinutes, range.totalMinutes) : PRIVATE} />
          <Stat label={`Value at ${fmtHourly(roi?.hourlyValueCents ?? model.hourlyValueCents)} an hour`} value={roi ? fmtValue(roi.valueCents) : PRIVATE} />
        </StatRow>
      </div>
      {rangeShown && range.networkingMinutesPerHeldChat != null ? (
        <Note className="mt-3">{`About ${minutesPhrase(range.networkingMinutesPerHeldChat)} of outreach and chat work saved per chat held.`}</Note>
      ) : null}
      {rangeShown && range.byKey.length ? <ActionBreakdown rows={range.byKey} minutesEach={minutesEach} /> : null}
      {rangeShown && !range.byKey.length ? <Note className="mt-3">No time saved in this range.</Note> : null}
      {!rangeShown ? <Note className="mt-3">The breakdown for this range is hidden to keep a small group anonymous.</Note> : null}
      {capacityHours > 0 ? (
        <Note className="mt-3">
          {`Advising-capacity equivalent: about ${capacityHours.toLocaleString('en-US')} ${capacityHours === 1 ? 'hour' : 'hours'} of chat prep and resume tailoring, about ${fmtValue(roi.capacity.valueCents)} at ${fmtHourly(roi.capacity.staffHourCents)} an hour, the BLS median wage for school and career counselors. A comparison of capacity, not time your office spent.`}
        </Note>
      ) : null}
      {contract ? (
        <Note className="mt-3">
          {`Contract: about ${fmtPerDollar(contract.valuePerDollar)} of student time for each $1 of contract cost in this range${contract.costPerHourCents == null ? '' : `, about ${dollars(contract.costPerHourCents)} of contract per hour saved`}.`}
        </Note>
      ) : null}
      {exact ? (
        <Note className="mt-3">Exact figures for platform administrators. Schools see these rounded, with small groups hidden.</Note>
      ) : null}
      {byPopulation ? (
        <>
          <Note className="mt-2">{`Program: ${byPopulation.program.students} students, ${fmtHours(byPopulation.program.rangeMinutes)} in this range, ${fmtHours(byPopulation.program.allTimeMinutes)} all time.`}</Note>
          <Note className="mt-2">{`Non-program: ${byPopulation.nonProgram.students} users, ${fmtHours(byPopulation.nonProgram.rangeMinutes)} in this range, ${fmtHours(byPopulation.nonProgram.allTimeMinutes)} all time.`}</Note>
        </>
      ) : null}
      <Explainer model={model} />
    </div>
  );
}
